import React, {
    useEffect,
    useState,
} from 'react';
import { INPUT, BTN_PRIMARY, BTN_SECONDARY_SM } from '@trinetra/shared';

import {
    ArrowPathIcon,
    ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

import * as rtmpApi from '../../services/rtmp.service';

interface RTMPPanelProps {
    nodeId: string;
    cameraId: string;
}

interface RTMPConfig {
    enable: boolean;
    stream_type: string;
    custom_enabled: boolean;
    address: string;
    port: number;
    custom_url: string;
    ssl_encrypt_enable: boolean;
    audio_simulation: boolean;
}

const inputCls = INPUT;

const labelCls =
    'text-[11px] font-medium text-muted-foreground mb-1 block';

const radioCls =
    'h-4 w-4 border-border text-brand focus:ring-brand-ring';

export default function RTMPPanel({
    nodeId,
    cameraId,
}: RTMPPanelProps) {
    const [loading, setLoading] =
        useState<boolean>(false);

    const [error, setError] =
        useState<string | null>(null);

    const [saveSuccess, setSaveSuccess] =
        useState<boolean>(false);

    const [vals, setVals] =
        useState<RTMPConfig>({
            enable: false,
            stream_type: 'Main',
            custom_enabled: false,
            address: '',
            port: 1935,
            custom_url: '',
            ssl_encrypt_enable: false,
            audio_simulation: false,
        });

    async function load() {
        if (!nodeId || !cameraId) {
            return;
        }

        setLoading(true);

        setError(null);

        try {
            const resp =
                await rtmpApi.getConfig(
                    nodeId,
                    cameraId
                );

            const cfg =
                resp.data?.data?.data ||
                resp.data?.data ||
                resp.data ||
                resp;

            setVals({
                enable:
                    cfg.enable || false,

                stream_type:
                    cfg.stream_type || 'Main',

                custom_enabled:
                    cfg.custom_enabled || false,

                address:
                    cfg.address || '',

                port:
                    cfg.port || 1935,

                custom_url:
                    cfg.custom_url || '',

                ssl_encrypt_enable:
                    cfg.ssl_encrypt_enable || false,

                audio_simulation:
                    cfg.audio_simulation || false,
            });

        } catch (err: any) {
            setError(
                err.response?.data?.error ||
                err.message ||
                'Failed to load RTMP configuration'
            );

        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        load();
    }, [nodeId, cameraId]);

    function update(
        key: keyof RTMPConfig,
        value: any
    ) {
        setVals((prev) => ({
            ...prev,
            [key]: value,
        }));
    }

    async function save() {
        if (!nodeId || !cameraId) {
            return;
        }

        setLoading(true);

        setError(null);

        setSaveSuccess(false);

        try {
            await rtmpApi.updateConfig(
                nodeId,
                cameraId,
                vals
            );

            setSaveSuccess(true);

            setTimeout(() => {
                setSaveSuccess(false);
            }, 3000);

        } catch (err: any) {
            setError(
                err.response?.data?.error ||
                err.message ||
                'Failed to save RTMP configuration'
            );

        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="mt-8 border-t border-border pt-6 space-y-5">

            {/* Header */}

            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-[14px] font-semibold text-foreground">
                        RTMP Configuration
                    </h3>

                    <p className="mt-1 text-[12px] text-muted-foreground">
                        Configure RTMP live streaming settings
                    </p>
                </div>

                <button
                    onClick={load}
                    disabled={loading}
                    className={BTN_SECONDARY_SM}
                >
                    <ArrowPathIcon
                        className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''
                            }`}
                    />

                    Refresh
                </button>
            </div>

            {/* Error */}

            {error && (
                <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 dark:border-red-800 dark:bg-red-900/20">
                    <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-500" />

                    <p className="text-[12px] text-red-700 dark:text-red-300">
                        {error}
                    </p>
                </div>
            )}

            {/* Success */}

            {saveSuccess && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-[12px] font-medium text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300">
                    ✓ RTMP configuration saved successfully
                </div>
            )}

            {/* Enable */}

            <label className="flex items-center gap-2.5 cursor-pointer">
                <input
                    type="checkbox"
                    checked={vals.enable}
                    onChange={(e) =>
                        update(
                            'enable',
                            e.target.checked
                        )
                    }
                    disabled={loading}
                    className="h-4 w-4 rounded border-border accent-brand"
                />

                <span className="text-[13px] text-foreground">
                    Enable RTMP
                </span>
            </label>

            {/* Stream Type */}

            <div>
                <label className={labelCls}>
                    Stream Type
                </label>

                <div className="mt-2 flex flex-wrap gap-6">
                    {[
                        {
                            label: 'Main Stream',
                            value: 'Main',
                        },
                        {
                            label: 'Sub Stream 1',
                            value: 'Sub1',
                        },
                        {
                            label: 'Sub Stream 2',
                            value: 'Sub2',
                        },
                        {
                            label: 'Sub Stream 3',
                            value: 'Sub3',
                        },
                    ].map((item) => (
                        <label
                            key={item.value}
                            className="flex items-center gap-2 cursor-pointer"
                        >
                            <input
                                type="radio"
                                name="stream_type"
                                value={item.value}
                                checked={
                                    vals.stream_type === item.value
                                }
                                onChange={(e) =>
                                    update(
                                        'stream_type',
                                        e.target.value
                                    )
                                }
                                disabled={loading}
                                className={radioCls}
                            />

                            <span className="text-[13px] text-foreground">
                                {item.label}
                            </span>
                        </label>
                    ))}
                </div>
            </div>

            {/* Address Type */}

            <div>
                <label className={labelCls}>
                    Address Type
                </label>

                <div className="mt-2 flex gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="radio"
                            checked={!vals.custom_enabled}
                            onChange={() =>
                                update(
                                    'custom_enabled',
                                    false
                                )
                            }
                            disabled={loading}
                            className={radioCls}
                        />

                        <span className="text-[13px] text-foreground">
                            Non-custom
                        </span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="radio"
                            checked={vals.custom_enabled}
                            onChange={() =>
                                update(
                                    'custom_enabled',
                                    true
                                )
                            }
                            disabled={loading}
                            className={radioCls}
                        />

                        <span className="text-[13px] text-foreground">
                            Custom
                        </span>
                    </label>
                </div>
            </div>

            {/* Inputs */}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                <div>
                    <label className={labelCls}>
                        IP Address
                    </label>

                    <input
                        value={vals.address}
                        onChange={(e) =>
                            update(
                                'address',
                                e.target.value
                            )
                        }
                        disabled={loading}
                        className={inputCls}
                    />
                </div>

                <div>
                    <label className={labelCls}>
                        Port
                    </label>

                    <input
                        type="number"
                        min="1"
                        max="65535"
                        value={vals.port}
                        onChange={(e) =>
                            update(
                                'port',
                                Number(e.target.value)
                            )
                        }
                        disabled={loading}
                        className={inputCls}
                    />
                </div>

            </div>

            {/* Custom URL */}

            {vals.custom_enabled && (
                <div>
                    <label className={labelCls}>
                        Custom Address
                    </label>

                    <input
                        value={vals.custom_url}
                        onChange={(e) =>
                            update(
                                'custom_url',
                                e.target.value
                            )
                        }
                        disabled={loading}
                        placeholder="rtmp://server/live/stream-key"
                        className={inputCls}
                    />
                </div>
            )}

            {/* Advanced */}

            <div className="flex flex-wrap gap-6">

                <label className="flex items-center gap-2.5 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={vals.ssl_encrypt_enable}
                        onChange={(e) =>
                            update(
                                'ssl_encrypt_enable',
                                e.target.checked
                            )
                        }
                        disabled={loading}
                        className="h-4 w-4 rounded border-border accent-brand"
                    />

                    <span className="text-[13px] text-foreground">
                        SSL Encryption
                    </span>
                </label>

                {/* <label className="flex items-center gap-2.5 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={vals.audio_simulation}
                        onChange={(e) =>
                            update(
                                'audio_simulation',
                                e.target.checked
                            )
                        }
                        disabled={loading}
                        className="h-4 w-4 rounded border-border accent-brand"
                    />

                    <span className="text-[13px] text-foreground">
                        Audio Simulation
                    </span>
                </label> */}

            </div>

            {/* Save Button */}

            <button
                onClick={save}
                disabled={loading}
                className={`${BTN_PRIMARY} w-full`}
            >
                {loading
                    ? 'Saving…'
                    : 'Save RTMP Configuration'}
            </button>

        </div>
    );
}